import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Briefcase,
  Calendar,
  FileText,
  Fingerprint,
  GraduationCap,
  Home,
  Inbox,
  Layers,
  Mail,
  Menu,
  Mic,
  Palette,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Star,
  Trophy,
  UserCircle,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { adminErrorsPing } from '../../lib/api';
import { BundleFreshness } from './BundleFreshness';
import { cn } from '../../lib/cn';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../lib/featureFlags';
import { useAuth } from '../../lib/useAuth';

const NAV = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/corpus', label: 'Corpus', icon: Layers },
  { to: '/dna', label: 'Career DNA', icon: Fingerprint },
  { to: '/presence', label: 'Get found', icon: Search },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/tracker', label: 'Tracker', icon: Trophy },
  { to: '/outreach', label: 'Outreach', icon: Mail },
  { to: '/prep', label: 'Prep', icon: Mic },
  { to: '/visa', label: 'Visa', icon: ScrollText },
  { to: '/profile', label: 'Profile', icon: UserCircle },
  { to: '/negotiation', label: 'Negotiate', icon: Briefcase },
  { to: '/references', label: 'References', icon: Star },
  { to: '/certs', label: 'Certs', icon: GraduationCap },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/style-guide', label: 'Style guide', icon: Palette },
] as const;

/**
 * Responsive shell. Mobile (< lg): hamburger top bar + slide-out drawer
 * sidebar over the content. Desktop (lg+): fixed sidebar rail, content
 * pushed right by lg:ml-72. The same nav markup is used both ways.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Admin-only Errors & Health nav item. We probe /api/errors/admin/ping (raw
  // fetch, silent on 403 — see adminErrorsPing) rather than reading a role off
  // /auth/me, so the nav appears for admins only and the probe never pollutes
  // error_logs for a non-admin.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    void adminErrorsPing().then((ok) => {
      if (alive) setIsAdmin(ok);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Hidden by default: the tactile style guide only appears when its OFF-default
  // flag is on (?corpusLive=1). Purely additive — the live nav is unchanged.
  const navItems = [
    ...NAV,
    ...(isFeatureEnabled(FEATURE_FLAGS.CORPUS_LIVE_STYLEGUIDE)
      ? [{ to: '/corpus-live', label: 'Corpus live', icon: Activity } as const]
      : []),
    ...(isAdmin ? [{ to: '/errors', label: 'Errors & Health', icon: AlertTriangle } as const] : []),
  ];

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Stale-tab cure: a quiet pill when the server ships a newer bundle. */}
      <BundleFreshness />
      {/* Mobile top bar — invisible on lg+ */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-ink-200 bg-white px-3 py-2 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="-ml-1 grid h-10 w-10 place-items-center rounded-lg hover:bg-ink-50 active:bg-ink-100"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-lg bg-brand-600 text-white">
            <FileText className="h-4 w-4" />
          </div>
          <div className="text-sm font-semibold">Career Optimizer</div>
        </div>
      </header>

      {/* Backdrop — mobile drawer only */}
      <div
        onClick={() => setOpen(false)}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        aria-hidden
      />

      {/* Sidebar — drawer on mobile, fixed rail on lg+ */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col overflow-y-auto border-r border-ink-200 bg-white px-4 py-5 transition-transform duration-200 ease-out',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Close button — mobile only */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          className="absolute right-2 top-2 grid h-10 w-10 place-items-center rounded-lg hover:bg-ink-50 lg:hidden"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 px-2 pb-6 pt-1">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-brand-600 text-white">
            <FileText className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">Career Optimizer</div>
            <div className="text-xs text-ink-500">Australia · v0.1</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <SignedInBadge />
        <div className="mt-3 rounded-xl border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
          <div className="mb-1 flex items-center gap-1 font-medium text-ink-700">
            <Calendar className="h-3.5 w-3.5" /> Sydney time
          </div>
          <ClockChip />
        </div>
      </aside>

      {/* Main — offset by sidebar width on lg+ */}
      <main className="overflow-auto lg:ml-72">{children}</main>
    </div>
  );
}

function SignedInBadge() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <NavLink
      to="/settings"
      className="mt-6 flex items-center gap-2 rounded-xl border border-ink-200 bg-white p-2 text-xs hover:border-brand-300 hover:bg-brand-50"
    >
      {user.picture ? (
        <img
          src={user.picture}
          alt=""
          className="h-7 w-7 shrink-0 rounded-full"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600">
          {(user.displayName ?? user.email)[0].toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-medium text-ink-800">{user.displayName ?? user.email}</div>
        <div className="truncate text-[11px] text-ink-500">{user.email}</div>
      </div>
    </NavLink>
  );
}

function ClockChip() {
  const now = new Date().toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return <div>{now}</div>;
}
